import type {
  CustomerAddressCreate,
  CustomerAddressUpdate,
  CustomerFavoriteList,
  CustomerFavoriteMutation,
  CustomerSupportRequestCreate,
  NotificationFeedItem,
  NotificationFeedPage,
  NotificationPreferences,
  NotificationPushPublicKey,
  NotificationRealtimeTicket,
  RegisterNotificationDeviceInput,
  UpdateNotificationPreferencesInput,
} from "@gobro/contracts";
import type {
  Cart,
  CustomerAddress,
  CustomerCatalog,
  CustomerOrder,
  Locale,
  Quote,
  StoreSummary,
  TrackingSnapshot,
} from "./domain";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly problem?: Record<string, unknown>,
  ) {
    super(message);
  }
}

interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  method?: "DELETE" | "GET" | "POST" | "PUT";
}

export class CustomerApi {
  constructor(readonly baseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api/v1") {}

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    let response: Response;
    try {
      const init: RequestInit = {
        credentials: "include",
        headers: {
          accept: "application/json",
          ...(options.body === undefined ? {} : { "content-type": "application/json" }),
          ...options.headers,
        },
        method: options.method ?? "GET",
      };
      if (options.body !== undefined) init.body = JSON.stringify(options.body);
      response = await fetch(`${this.baseUrl}${path}`, init);
    } catch (error) {
      throw new ApiError(error instanceof Error ? error.message : "Network unavailable", 0);
    }
    const contentType = response.headers.get("content-type") ?? "";
    const data = contentType.includes("json") ? await response.json() : await response.text();
    if (!response.ok) {
      const problem =
        typeof data === "object" && data !== null ? (data as Record<string, unknown>) : undefined;
      const detail = problem && typeof problem.detail === "string" ? problem.detail : String(data);
      throw new ApiError(detail || `HTTP ${response.status}`, response.status, problem);
    }
    return data as T;
  }

  me() {
    return this.request<{ locale: Locale; phoneE164: string; userId: string }>("/auth/me");
  }

  requestOtp(phoneE164: string) {
    return this.request<{ challengeId: string }>("/auth/otp/request", {
      body: { phoneE164, purpose: "sign_in" },
      method: "POST",
    });
  }

  verifyOtp(challengeId: string, code: string) {
    return this.request<{ user: { locale: Locale; phoneE164: string; userId: string } }>(
      "/auth/otp/verify",
      {
        body: { challengeId, clientType: "web", code, deviceName: "GoBro Customer PWA" },
        method: "POST",
      },
    );
  }

  stores(
    locale: Locale,
    location: { latitude: number; longitude: number },
    q = "",
    businessType = "",
  ) {
    const params = new URLSearchParams({
      latitude: String(location.latitude),
      limit: "100",
      locale,
      longitude: String(location.longitude),
    });
    if (q.trim().length >= 2) params.set("q", q.trim());
    if (businessType) params.set("businessType", businessType);
    return this.request<{ items: StoreSummary[]; nextCursor: string | null }>(
      `/catalog/stores?${params}`,
    );
  }

  catalog(slug: string, locale: Locale) {
    return this.request<CustomerCatalog>(`/catalog/stores/${slug}?locale=${locale}`);
  }

  serviceability(slug: string, location: { latitude: number; longitude: number }) {
    return this.request<{ serviceable: boolean; zone: { id: string; name: string } | null }>(
      `/catalog/stores/${slug}/serviceability?latitude=${location.latitude}&longitude=${location.longitude}`,
    );
  }

  addresses() {
    return this.request<{ items: CustomerAddress[] }>("/customer/addresses");
  }

  createAddress(input: CustomerAddressCreate) {
    return this.request<CustomerAddress>("/customer/addresses", { body: input, method: "POST" });
  }

  updateAddress(id: string, input: CustomerAddressUpdate) {
    return this.request<CustomerAddress>(`/customer/addresses/${id}`, {
      body: input,
      method: "PUT",
    });
  }

  deleteAddress(id: string) {
    return this.request<{ deleted: true }>(`/customer/addresses/${id}`, { method: "DELETE" });
  }

  quote(cart: Cart, address: CustomerAddress, locale: Locale, idempotencyKey: string) {
    return this.request<Quote>("/quotes", {
      body: {
        destination: address.location,
        items: cart.lines.map((line) => ({
          itemId: line.itemId,
          modifierOptionIds: line.modifierOptionIds,
          quantity: line.quantity,
        })),
        locale,
        storeId: cart.storeId,
      },
      headers: { "idempotency-key": idempotencyKey },
      method: "POST",
    });
  }

  placeOrder(
    quoteId: string,
    deliveryAddress: string,
    paymentMethod: "cash_on_delivery" | "sandbox_card",
    note: string | null,
    idempotencyKey: string,
  ) {
    return this.request<CustomerOrder>("/orders", {
      body: { deliveryAddress, note, paymentMethod, quoteId },
      headers: { "idempotency-key": idempotencyKey },
      method: "POST",
    });
  }

  orders() {
    return this.request<{ items: CustomerOrder[]; nextAfter: string | null }>("/orders?limit=100");
  }

  order(id: string) {
    return this.request<CustomerOrder>(`/orders/${id}`);
  }

  cancelOrder(
    id: string,
    version: number,
    reason: "customer_changed_mind" | "customer_duplicate_order",
  ) {
    return this.request<CustomerOrder>(`/orders/${id}/cancel`, {
      body: { expectedVersion: version, reason },
      headers: { "idempotency-key": `customer-cancel-${crypto.randomUUID()}` },
      method: "POST",
    });
  }

  trackingSnapshot(id: string) {
    return this.request<
      | { available: false; orderId: string; orderStatus: string }
      | { available: true; snapshot: TrackingSnapshot }
    >(`/orders/${id}/tracking/snapshot`);
  }

  trackingTicket(id: string) {
    return this.request<{ expiresAt: string; heartbeatSeconds: number; ticket: string }>(
      `/orders/${id}/tracking/realtime-ticket`,
      { method: "POST" },
    );
  }

  async streamTracking(
    ticket: string,
    afterCursor: number,
    signal: AbortSignal,
    onSnapshot: (snapshot: TrackingSnapshot) => void,
  ): Promise<void> {
    const response = await fetch(
      `${this.baseUrl}/tracking/events?afterCursor=${encodeURIComponent(afterCursor)}`,
      {
        credentials: "include",
        headers: { accept: "text/event-stream", authorization: `Realtime ${ticket}` },
        signal,
      },
    );
    if (!response.ok || !response.body)
      throw new ApiError("Live tracking unavailable", response.status);
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      const messages = buffer.split("\n\n");
      buffer = messages.pop() ?? "";
      for (const message of messages) {
        const dataLine = message.split("\n").find((line) => line.startsWith("data: "));
        if (!dataLine) continue;
        const wire = JSON.parse(dataLine.slice(6)) as {
          data?: TrackingSnapshot | { snapshot?: TrackingSnapshot };
          event?: string;
        };
        if (wire.event === "tracking.event.v1" && wire.data && "snapshot" in wire.data) {
          if (wire.data.snapshot) onSnapshot(wire.data.snapshot);
        } else if (
          (wire.event === "tracking.ready.v1" || wire.event === "tracking.resync_required.v1") &&
          wire.data &&
          "assignmentId" in wire.data
        ) {
          onSnapshot(wire.data as TrackingSnapshot);
        }
      }
    }
  }

  favorites() {
    return this.request<CustomerFavoriteList>("/customer/favorites");
  }

  addFavorite(storeId: string) {
    return this.request<CustomerFavoriteMutation>(`/customer/favorites/${storeId}`, {
      method: "PUT",
    });
  }

  removeFavorite(storeId: string) {
    return this.request<CustomerFavoriteMutation>(`/customer/favorites/${storeId}`, {
      method: "DELETE",
    });
  }

  createSupport(input: CustomerSupportRequestCreate) {
    return this.request("/customer/support/requests", { body: input, method: "POST" });
  }

  supportRequests() {
    return this.request<{
      items: Array<{ id: string; status: string; subject: string; createdAt: string }>;
    }>("/customer/support/requests");
  }

  notificationPushKey() {
    return this.request<NotificationPushPublicKey>("/notifications/push/public-key");
  }

  registerNotificationDevice(input: RegisterNotificationDeviceInput) {
    return this.request<{ deviceId: string; platform: string }>("/notifications/devices", {
      body: input,
      method: "POST",
    });
  }

  notificationFeed(cursor: string | null, limit = 25) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set("cursor", cursor);
    return this.request<NotificationFeedPage>(`/notifications/feed?${params}`);
  }

  markNotificationRead(id: string) {
    return this.request<NotificationFeedItem>(`/notifications/feed/${id}/read`, { method: "POST" });
  }

  markAllNotificationsRead() {
    return this.request<{ updated: number }>("/notifications/feed/read-all", { method: "POST" });
  }

  notificationPreferences() {
    return this.request<NotificationPreferences>("/notifications/preferences");
  }

  updateNotificationPreferences(input: UpdateNotificationPreferencesInput) {
    return this.request<NotificationPreferences>("/notifications/preferences", {
      body: input,
      method: "PUT",
    });
  }

  notificationRealtimeTicket() {
    return this.request<NotificationRealtimeTicket>("/notifications/realtime-ticket", {
      method: "POST",
    });
  }

  logout() {
    return this.request<void>("/auth/logout", { method: "POST" });
  }
}
